import { Container, ContainerGutter, ContainerPadding, Divider, FilterTag } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useOfertasFilterContext } from '../../context/ofertas-filter-context';
import { capitalizeSentence } from '../../helpers/ofertas-helper';
import { useOfertasTracker } from '../../helpers/use-ofertas-tracker';
import { FilterTypes, gamaTypes, motorizacionTypes } from '../../services/ofertas-service/models/ofertas';

export const DesktopSelector: React.FC = () => {
	const {
		handleFilteredCarlines,
		selectedGamaFilter,
		selectedMotorizacionFilter,
		setSelectedGamaFilter,
		setSelectedMotorizacionFilter,
		setActiveCarlineIndex,
		anchorScrollOnFilter,
		gamaFilterTypes,
		motorizacionFilterTypes,
		isCombinationAvailable,
		calculateOfertasNumberOnFilter,
	} = useOfertasFilterContext();

	const [currentIndex, setCurrentIndex] = useState(0);

	const { trackOfertasFilterSelectClick } = useOfertasTracker();

	const shouldShowMotorizacionButtons = () => {
		return motorizacionFilterTypes.filter(Boolean).length > 1;
	};

	const shouldShowGamaButtons = () => {
		return gamaFilterTypes.filter(Boolean).length > 1;
	};

	const showSelectorOptions = () => {
		return shouldShowMotorizacionButtons() || shouldShowGamaButtons();
	};

	const shouldShowTodosButton = () => {
		return shouldShowMotorizacionButtons() || shouldShowGamaButtons();
	};

	const handleOnClick = (selector: string, index?: number) => {
		// No ejecutar el filtro si ya está activo
		const isTodosAlreadyActive = selector === FilterTypes.TODOS && !selectedGamaFilter && !selectedMotorizacionFilter;
		const isFilterAlreadyActive = selector === selectedGamaFilter || selector === selectedMotorizacionFilter;
		if (isTodosAlreadyActive || isFilterAlreadyActive) {
			return;
		}

		anchorScrollOnFilter();
		setActiveCarlineIndex(0);

		if (selector === FilterTypes.TODOS) {
			setSelectedGamaFilter(null);
			setSelectedMotorizacionFilter(null);
		} else if (gamaTypes.includes(selector as FilterTypes)) {
			setSelectedGamaFilter(selector);
		} else if (motorizacionTypes.includes(selector as FilterTypes)) {
			setSelectedMotorizacionFilter(selector);
		}

		trackOfertasFilterSelectClick(selector);
		handleFilteredCarlines(selector);

		if (index) {
			setCurrentIndex(index);
		}
	};

	useEffect(() => {
		const scrollContainer = document.getElementById('scroll-filter-menu');

		const allFilters = [...motorizacionTypes, ...gamaTypes];
		const activeFilter = selectedGamaFilter || selectedMotorizacionFilter;
		const listPosition = activeFilter ? allFilters.indexOf(activeFilter as FilterTypes) : 0;

		let scrollAmount = 20;

		switch (listPosition) {
			case 1:
				scrollAmount = 10;
				break;
			case 2:
				scrollAmount = 100;
				break;
			case 3:
				scrollAmount = 130;
				break;
			case 4:
				scrollAmount = 160;
				break;
			case 5:
				scrollAmount = 190;
				break;
			default:
				break;
		}

		if (scrollContainer) {
			scrollContainer.scroll({
				left: scrollAmount,
				behavior: 'smooth',
			});
		}
	}, [currentIndex, selectedGamaFilter, selectedMotorizacionFilter]);

	return (
		<Container
			data-testid="filter-selector"
			padding={{
				top: ContainerPadding.dynamic0150,
			}}
		>
			{showSelectorOptions() && (
				<Container wrap={'never'} gutter={ContainerGutter.static200}>
					{shouldShowTodosButton() && (
						<FilterTag
							text={`${motorizacionTypes[0]} (${calculateOfertasNumberOnFilter(null, null)})`}
							active={!selectedGamaFilter && !selectedMotorizacionFilter}
							onClick={() => handleOnClick(motorizacionTypes[0])}
							emphasis={'secondary'}
						/>
					)}

					{shouldShowGamaButtons() && (
						<Container wrap={'never'} gutter={ContainerGutter.static200}>
							<Divider isVertical />
							{gamaFilterTypes.includes(FilterTypes.PROFESIONAL) && (
								<FilterTag
									text={`${capitalizeSentence(gamaTypes[0])} (${calculateOfertasNumberOnFilter(gamaTypes[0], selectedMotorizacionFilter)})`}
									active={selectedGamaFilter === gamaTypes[0]}
									onClick={() => handleOnClick(gamaTypes[0])}
									disabled={!isCombinationAvailable(gamaTypes[0], selectedMotorizacionFilter)}
									emphasis={'secondary'}
								/>
							)}
							{gamaFilterTypes.includes(FilterTypes.LIFESTYLE) && (
								<FilterTag
									text={`${capitalizeSentence(gamaTypes[1])} (${calculateOfertasNumberOnFilter(gamaTypes[1], selectedMotorizacionFilter)})`}
									active={selectedGamaFilter === gamaTypes[1]}
									onClick={() => handleOnClick(gamaTypes[1])}
									disabled={!isCombinationAvailable(gamaTypes[1], selectedMotorizacionFilter)}
									emphasis={'secondary'}
								/>
							)}
							<Divider isVertical />
						</Container>
					)}

					{shouldShowMotorizacionButtons() && (
						<Container wrap={'never'} gutter={ContainerGutter.static200}>
							{motorizacionFilterTypes.includes(FilterTypes.ELECTRICOS) && (
								<FilterTag
									text={`${motorizacionTypes[1]} (${calculateOfertasNumberOnFilter(selectedGamaFilter, motorizacionTypes[1])})`}
									active={selectedMotorizacionFilter === motorizacionTypes[1]}
									onClick={() => handleOnClick(motorizacionTypes[1])}
									disabled={!isCombinationAvailable(selectedGamaFilter, FilterTypes.ELECTRICOS)}
									emphasis={'secondary'}
								/>
							)}
							{motorizacionFilterTypes.includes(FilterTypes.HIBRIDOS_ENCHUFABLES) && (
								<FilterTag
									text={`${motorizacionTypes[2]} (${calculateOfertasNumberOnFilter(selectedGamaFilter, motorizacionTypes[2])})`}
									active={selectedMotorizacionFilter === motorizacionTypes[2]}
									onClick={() => handleOnClick(motorizacionTypes[2])}
									disabled={!isCombinationAvailable(selectedGamaFilter, FilterTypes.HIBRIDOS_ENCHUFABLES)}
									emphasis={'secondary'}
								/>
							)}
							{motorizacionFilterTypes.includes(FilterTypes.COMBUSTION) && (
								<FilterTag
									text={`${motorizacionTypes[3]} (${calculateOfertasNumberOnFilter(selectedGamaFilter, motorizacionTypes[3])})`}
									active={selectedMotorizacionFilter === motorizacionTypes[3]}
									onClick={() => handleOnClick(motorizacionTypes[3])}
									disabled={!isCombinationAvailable(selectedGamaFilter, FilterTypes.COMBUSTION)}
									emphasis={'secondary'}
								/>
							)}
						</Container>
					)}
				</Container>
			)}
		</Container>
	);
};
